import { PublicBundleData, PriceRule } from '../types/bundleData';
import { BaseProduct, BaseProductVariant, BundleData as StorefrontBundleData } from '../types/bundle';

const mapBundleSettings = (bundleSettings: StorefrontBundleData['bundle_settings']) => {
  const addons = bundleSettings.layout_settings.addons;
  const crossSells = bundleSettings.layout_settings.crossSells;
  const customizationWindow = bundleSettings.customization_window;
  const defaultVariantId = bundleSettings.default_bundle_variant_id;

  return {
    default_variant_id: defaultVariantId ? String(defaultVariantId) : null,
    is_customizable: bundleSettings.is_customizable,
    price_rule: String(bundleSettings.price_rule).toUpperCase(),
    customization_window: customizationWindow
      ? {
          active_days: customizationWindow,
          disabled_message: bundleSettings.customization_window_disabled_message || '',
        }
      : null,
    addons: addons.collectionId
      ? { collection_id: addons.collectionId, collection_handle: addons.collectionHandle }
      : null,
    cross_sells: crossSells.collectionId
      ? { collection_id: addons.collectionId, collection_handle: addons.collectionHandle }
      : null,
  };
};

const mapIncentives = (incentives: StorefrontBundleData['incentives']): PublicBundleData['incentives'] => {
  if (incentives.tiered_discounts.length === 0) return null;

  return {
    tiered_discounts: incentives.tiered_discounts.map(tieredDiscount => ({
      eligible_charge_types: tieredDiscount.eligible_charge_types,
      eligible_line_item_types: tieredDiscount.eligible_line_item_types,
      external_product_id: tieredDiscount.external_product_id,
      name: tieredDiscount.name,
      status: tieredDiscount.status,
      tiers: tieredDiscount.tiers.map(tiered => ({
        discount_type: tiered.discount_type,
        discount_value: tiered.discount_value,
        condition_quantity_gte: tiered.condition_quantity_gte,
      })),
    })),
  };
};

const mapSellingPlan = (
  sellingPlan: StorefrontBundleData['selling_plan_groups'][number]['selling_plans'][number]
): PublicBundleData['selling_plan_groups'][number]['selling_plans'][number] => ({
  id: sellingPlan.id,
  name: sellingPlan.name,
  options: sellingPlan.options.map(option => ({ name: option.name, values: option.values })),
  recurring_deliveries: sellingPlan.recurring_deliveries,
  price_adjustments: sellingPlan.price_adjustments.map(({ value, value_type }) => ({
    value,
    value_type,
  })),
});

const mapSellingPlanGroups = (
  sellingPlanGroup: StorefrontBundleData['selling_plan_groups'][number]
): PublicBundleData['selling_plan_groups'][number] => ({
  name: sellingPlanGroup.name,
  options: sellingPlanGroup.options.map(option => ({ name: option.name, values: option.values })),
  selling_plans: sellingPlanGroup.selling_plans.map(mapSellingPlan),
});

const mapBasicVariant = (variant: BaseProductVariant) => ({
  id: String(variant.id),
  title: variant.title,
  price: variant.price,
  compare_at_price: variant.compare_at_price,
  options: variant.options,
  available_for_sale: variant.available,
});

const mapBasicProduct = (product: BaseProduct) => ({
  id: String(product.id),
  title: product.title,
  description: product.description,
  available_for_sale: product.available,
  featured_image: product.featured_image,
  images: product.images,
  requires_selling_plan: product.requires_selling_plan,
  selling_plan_groups: product.selling_plan_groups.map(mapSellingPlanGroups),
});

const mapProduct = (product: StorefrontBundleData['collections'][number]['products'][number]) => {
  const productPlansLookup = productSellingPlansGroupLookup(product.selling_plan_groups);

  return {
    ...mapBasicProduct(product),
    handle: product.handle,
    tags: product.tags,
    price_range: { min: product.price_min, max: product.price_max },
    compare_at_price_range: { min: product.compare_at_price_min, max: product.compare_at_price_max },
    options: product.options,
    variants: product.variants.map(variant => ({
      ...mapBasicVariant(variant),
      selling_plan_allocations: variant.selling_plan_allocations.map(sellingPlan => ({
        price: sellingPlan.price,
        compare_at_price: sellingPlan.compare_at_price,
        selling_plan: mapSellingPlan(
          productPlansLookup[`${sellingPlan.selling_plan_group_id}-${sellingPlan.selling_plan_id}`]
        ),
      })),
      option1: variant.option1,
      option2: variant.option2,
      option3: variant.option3,
      sku: variant.sku,
      requires_shipping: variant.requires_shipping,
      taxable: variant.taxable,
      featured_image: variant.featured_image,
      public_title: variant.public_title,
      requires_selling_plan: variant.requires_selling_plan,
    })),
  };
};

const mapBundleVariantRanges = ({
  items_count,
  ranges,
}: Pick<StorefrontBundleData['bundle_settings']['variants'][number], 'items_count' | 'ranges'>) => {
  if (ranges.length > 0) {
    return ranges.map(({ quantity_max, quantity_min }) => ({ min: quantity_min, max: quantity_max }));
  }

  return [{ min: items_count, max: items_count }];
};

const productSellingPlansGroupLookup = (sellingPlanGroups: BaseProduct['selling_plan_groups']) =>
  sellingPlanGroups.reduce<
    Record<string, StorefrontBundleData['selling_plan_groups'][number]['selling_plans'][number]>
  >((acc, current) => {
    current.selling_plans.forEach(sellingPlan => {
      const key = `${current.id}-${sellingPlan.id}`;
      acc[key] = sellingPlan;
    });

    return acc;
  }, {});

export function mapOnlineStoreToPublicBundleData(bundleData: StorefrontBundleData): PublicBundleData {
  const bundleSettings = mapBundleSettings(bundleData.bundle_settings);
  const variantsLookup = bundleData.variants.reduce<Record<string, StorefrontBundleData['variants'][number]>>(
    (acc, variant) => {
      acc[variant.id] = variant;
      return acc;
    },
    {}
  );

  return {
    id: String(bundleData.id),
    title: bundleData.title,
    handle: bundleData.handle,
    options: bundleData.options.map(option => ({
      name: option.name,
      position: option.position,
      values: option.values,
    })),
    default_variant_id: bundleSettings.default_variant_id,
    available_for_sale: bundleData.available,
    requires_selling_plan: bundleData.requires_selling_plan,
    bundle_settings: {
      is_customizable: bundleSettings.is_customizable,
      price_rule: bundleSettings.price_rule as PriceRule,
      customization_window: bundleSettings.customization_window,
      max_quantity_per_variant: null,
    },
    variants: bundleData.bundle_settings.variants
      .filter(({ enabled }) => enabled)
      .map((item, index) => {
        const variant = variantsLookup[String(item.external_variant_id)];

        return {
          id: String(item.external_variant_id),
          title: variant.title,
          price: variant.price,
          compare_at_price: variant.compare_at_price,
          image: variant.image || '',
          available_for_sale: variant.available,
          options: variant.options,
          requires_selling_plan: variant.requires_selling_plan,
          selling_plan_allocations: variant.selling_plan_allocations.map(sellingPlan => ({
            price: sellingPlan.price,
            compare_at_price: sellingPlan.compare_at_price,
            selling_plan: mapSellingPlan(sellingPlan.selling_plan),
          })),
          ranges: mapBundleVariantRanges(item),
          collections: item.option_sources.map(source => ({
            id: source.option_source_id,
            source_platform: 'shopify',
            min: source.quantity_min || 0,
            max: source.quantity_max,
          })),
          default_selections: item.selection_defaults.map(selectionDefault => ({
            quantity: selectionDefault.quantity,
            product: {
              id: String(selectionDefault.product.id),
              handle: selectionDefault.handle,
              title: selectionDefault.product.title,
              description: selectionDefault.product.description,
              images: selectionDefault.product.images,
            },
            variant: {
              id: selectionDefault.external_variant_id,
              title: selectionDefault.variant.title,
              featured_image: selectionDefault.variant.featured_image,
              sku: selectionDefault.sku,
            },
          })),
          position: index,
        };
      }),
    selling_plan_groups: bundleData.selling_plan_groups.map(mapSellingPlanGroups),
    collections: Object.values(bundleData.collections).reduce<PublicBundleData['collections']>((acc, current) => {
      acc[String(current.id)] = {
        id: String(current.id),
        title: current.title,
        handle: current.handle,
        products: current.products.map(mapProduct),
      };

      return acc;
    }, {}),
    addons: bundleData.addons.products.length
      ? {
          collection_id: bundleSettings.addons?.collection_id || '',
          collection_handle: bundleSettings.addons?.collection_handle || '',
          products: bundleData.addons.products.map(product => ({
            ...mapBasicProduct(product),
            price_range: { min: product.price, max: product.price },
            compare_at_price_range: { min: product.compare_at_price || 0, max: product.compare_at_price },
            variants: product.variants.map(variant => ({
              ...mapBasicVariant(variant),
              image: variant.image,
              selling_plan_allocations: variant.selling_plan_allocations.map(sellingPlan => ({
                price: sellingPlan.price,
                compare_at_price: sellingPlan.compare_at_price,
                selling_plan: mapSellingPlan(sellingPlan.selling_plan),
              })),
            })),
          })),
        }
      : null,
    cross_sells: bundleData.cross_sells.products.length
      ? {
          collection_id: bundleSettings.cross_sells?.collection_id || '',
          collection_handle: bundleSettings.cross_sells?.collection_handle || '',
          products: bundleData.cross_sells.products.map(mapProduct),
        }
      : null,
    incentives: mapIncentives(bundleData.incentives),
  };
}
